import React, { useRef, useEffect } from 'react';
import { usePracticeViewerState } from '../../../../../store/hooks/dataEvents';
import ReactDOM from 'react-dom';

import { LIVE_PRACTICE, DEFAULT_TIMELINE_TIME, PAST_GAME } from '../../../../../config/constants';
import { getRealTimeActualSeconds, getTimelinePosition, getPracticeTimestamp, getActiveTiming, removeDecimalsFromSecs, tsDifferenceInSecs, isNotCurrentlyLive } from '../../../../../helpers/helpers';
import { drillStyles } from '../mapStyles/mapStyles';
import { Timeline, Blinks } from '../../types';
import { DataMeta, Drill } from '../../../../../config/commonTypes';

export default ({
  currentSec, setCurrentSec, currentPeriodIndex, playersTrails, liveCurrentSec, setPlayersTrails,
  isNowLive, setIsNowLive, setOnPlay, onClickEventsDispatcher, getCurrentTimePeriod
} : Timeline) => {

  const slider = useRef(null);
  const { blinks, practiceData } : { blinks: Blinks, practiceData: DataMeta}  = usePracticeViewerState();

  useEffect(() => {
    const maxValue = getPracticeTimeEnd(practiceData.startTime, practiceData.endTime);
    // eslint-disable-next-line react/no-find-dom-node
    const sliderBar = ReactDOM.findDOMNode(slider.current) as HTMLElement;
    const progressBarPer = (100 * currentSec) / maxValue;

    sliderBar.style.backgroundImage = `linear-gradient(to right, #378AD8 ${progressBarPer}%, transparent 0%)`;
    validateIfIsLive();
  }, [currentSec, liveCurrentSec]);

  const validateIfIsLive = () => {
    if (practiceData.type === LIVE_PRACTICE || practiceData.type === PAST_GAME) {
      // 1s in decimals to have an error margin between the PracticeMap tracks loader vs the timeline
      if (isNotCurrentlyLive(currentSec, liveCurrentSec)) {
        if (isNowLive) setIsNowLive(false);
      } else  {
        if (!isNowLive) setIsNowLive(true);
      }
    }
  };

  const handleProgressBar = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { startTime, type, drills} = practiceData;
    const updatedSec = parseInt(event.target.value);
    setCurrentSec(updatedSec);
    getCurrentTimePeriod(startTime, drills, type);
  };

  const getPracticeTimeEnd = (start: number, end: number) => {
    if (practiceData.type === LIVE_PRACTICE || practiceData.type === PAST_GAME) {
      return liveCurrentSec;
    } else if (start && end) {
      const secs = parseInt(((end - start) / 1000).toFixed(0));
      return (secs * 10) - 1;
    } else {
      return 0;
    }
  };

  const handleOnMouseUp = () => {
    const secs = removeDecimalsFromSecs(currentSec);
    if (!blinks || !blinks[secs]) {
      onClickEventsDispatcher(currentSec / 10); // Get seconds w/decimals
    } else {
      // clean all the future trails
      const unmutedTrails = { ...playersTrails };
      Object.entries(playersTrails).forEach(([playerId, playerTrails]) => {
        Object.keys(playerTrails).forEach(trailKey => {
          if (parseFloat(trailKey) > currentSec) {
            delete unmutedTrails[playerId][trailKey];
          }
        });
      });

      // update state only if the trails object has changed
      if (JSON.stringify(playersTrails) !== JSON.stringify(unmutedTrails)) {
        setPlayersTrails(unmutedTrails);
      }
    }
  };

  const handleOnLiveClick = () => {
    const { actualSeconds } = getRealTimeActualSeconds(practiceData.startTime);
    setOnPlay(true);
    onClickEventsDispatcher(removeDecimalsFromSecs(actualSeconds));
  };

  const showPracticeEndTime = (endTime: number) => {
    if (practiceData.type === LIVE_PRACTICE || practiceData.type === PAST_GAME) {
      return (
        <div onClick={() => handleOnLiveClick()} className="live-text" style={{ color: isNowLive ? '#FF5F5F' : ''}}>
          <div className="live-indicator" style={{ backgroundColor: isNowLive ? '#FF5F5F' : ''}} />
          LIVE
        </div>
      );
    } else if (endTime) {
      return getPracticeTimestamp(-1, endTime);
    } else {
      return DEFAULT_TIMELINE_TIME;
    }
  };

  const setPeriodDrillsStyles = (drill: Drill, maxValue: number) => {
    const { startTs } = drill;
    const { startTime } = practiceData;

    const periodInSecs = tsDifferenceInSecs(startTime, startTs) * 10;
    const periodTimeline = getTimelinePosition(slider, periodInSecs, maxValue) as number;
    const shouldShowTL = maxValue * 0.0025;

    if (periodTimeline >= 0) {
      return drillStyles(periodTimeline, periodInSecs, currentSec, shouldShowTL);
    }
  };

  const handleDrillClicked = (drill: Drill, index: number) => {
    const { startTs } = drill;
    const { startTime } = practiceData;
    const periodInSecs = tsDifferenceInSecs(startTime, startTs);
    onClickEventsDispatcher(periodInSecs, index);
  };

  const getTimelineLocation = (maxValue: number) => {
    const periodTimeline = getTimelinePosition(slider, currentSec, maxValue) as number;
    if (periodTimeline >= 0) return { marginLeft: periodTimeline - 20 };
  };

  const { startTime, endTime, drills } = practiceData;
  const { secs, /* decs */ } = getActiveTiming(currentSec);
  const maxValue = getPracticeTimeEnd(startTime, endTime);
  const { actualTime } = getRealTimeActualSeconds(startTime);

  return (
    <div className="timeline-container">
      <input
        ref={slider}
        className={"slider"}
        type="range"
        min={0}
        max={maxValue}
        value={currentSec}
        onChange={handleProgressBar}
        onMouseUp={handleOnMouseUp} />

      <div className="tickmarks">
        {drills && drills.filter(drill => drill.startTs && drill.startTs <= actualTime)
        .map((drill, index) => {
          return <div
          key={drill.session_id}
          onClick={() => handleDrillClicked(drill, index)}
          style={setPeriodDrillsStyles(drill, maxValue)} />;
        })}
      </div>

      {practiceData.type !== PAST_GAME && drills && drills !== undefined && (
        <div className="period-text-content" style={getTimelineLocation(maxValue)}>
          {drills[currentPeriodIndex].drillType? drills[currentPeriodIndex].drillType : '-'}
        </div>
      )}

      <span className="time-text">
        <span data-status={ startTime ? "active" : "disabled"}>
          { startTime ? getPracticeTimestamp(secs, startTime) : DEFAULT_TIMELINE_TIME }/
        </span>
        <span data-status={ endTime ? "end" : "disabled"}>
          { showPracticeEndTime(endTime) }
        </span>
      </span>
    </div>
  );
};
